import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../utils/apiConfig';
import {
  User, Lock, Settings2, Check, AlertCircle, Globe, Sun, Moon, Eye, EyeOff,
  Database, Shield, Download, Trash2, Building2, Calendar, Package, FileInput, FlaskConical, Target, Loader2,
} from 'lucide-react';
import DeleteAccountModal from '../components/account/DeleteAccountModal';

const SectionCard = ({ icon: Icon, title, isDark, children }) => (
  <div className={`rounded-2xl border p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
    <div className="flex items-center gap-3 mb-6">
      <div className={`p-2 rounded-xl ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
        <Icon className={`w-5 h-5 ${isDark ? 'text-slate-300' : 'text-gray-600'}`} />
      </div>
      <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
    </div>
    {children}
  </div>
);

const FeedbackMessage = ({ type, message, isDark }) => {
  if (!message) return null;
  const isSuccess = type === 'success';
  return (
    <div className={`flex items-center gap-2 mt-4 px-4 py-3 rounded-xl text-sm ${
      isSuccess
        ? isDark ? 'bg-green-500/10 text-green-400' : 'bg-green-50 text-green-700'
        : isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-700'
    }`}>
      {isSuccess ? <Check className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
      <span>{message}</span>
    </div>
  );
};

const PasswordField = ({ id, label, value, onChange, isDark, placeholder }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div>
      <label htmlFor={id} className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`w-full px-4 py-2.5 pr-10 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500 focus:outline-none ${
            isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
          }`}
        />
        <button
          type="button"
          onClick={() => setVisible(v => !v)}
          className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400 hover:text-gray-600'}`}
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

const Account = () => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { t, language, toggleLanguage } = useLanguage();
  const { user, setUser, deleteAccount } = useAuth();

  // Profile section
  const [name, setName] = useState(user?.name || '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  // Security section
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);

  // RGPD-2.1 — data summary (Mes données section)
  const [dataSummary, setDataSummary] = useState(null);
  const [loadingDataSummary, setLoadingDataSummary] = useState(true);

  // RGPD-2.2 — export ZIP state
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  // RGPD-2.3 — delete account modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleAccountDeleted = () => {
    // Backend a déjà clear le cookie httpOnly. On clear le state local
    // + redirect vers /auth (compte désactivé, login impossible désormais).
    setUser(null);
    navigate('/auth');
  };

  useEffect(() => {
    let cancelled = false;
    const fetchDataSummary = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/auth/me/data-summary`);
        if (!cancelled) setDataSummary(response.data);
      } catch (err) {
        if (!cancelled) console.error('Failed to fetch data summary:', err);
      } finally {
        if (!cancelled) setLoadingDataSummary(false);
      }
    };
    fetchDataSummary();
    return () => { cancelled = true; };
  }, []);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/me/export`,
        {},
        { responseType: 'blob' },
      );
      // Trigger browser download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Extract filename from Content-Disposition (fallback if absent)
      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      a.download = filenameMatch
        ? filenameMatch[1]
        : `my-data-export-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Blob response : detail must be parsed from blob
      let detail = null;
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          detail = JSON.parse(text)?.detail || null;
        } catch {
          detail = null;
        }
      } else {
        detail = err.response?.data?.detail || null;
      }
      if (err.response?.status === 429) {
        setExportError(detail || t('account.privacy.exportRateLimit'));
      } else {
        setExportError(detail || t('account.privacy.exportError'));
      }
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const locale = language === 'fr' ? 'fr-FR' : 'de-DE';
      return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileMsg(null);
    try {
      const res = await axios.put(`${API_URL}/api/auth/me`, { name });
      setUser(prev => ({ ...prev, name: res.data.name }));
      setProfileMsg({ type: 'success', text: t('account.profileSaved') });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.response?.data?.detail || t('common.error') });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg(null);
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: t('account.passwordMismatch') });
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPasswordMsg({ type: 'success', text: t('account.passwordChanged') });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordMsg({ type: 'error', text: err.response?.data?.detail || t('common.error') });
    } finally {
      setPasswordLoading(false);
    }
  };

  const inputCls = `w-full px-4 py-2.5 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500 focus:outline-none ${
    isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-gray-200 text-gray-900'
  }`;

  const labelCls = `block text-sm font-medium mb-1.5 ${isDark ? 'text-slate-300' : 'text-gray-700'}`;

  return (
    <div className="max-w-2xl space-y-8">
      {/* Header */}
      <div>
        <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t('account.title')}
        </h1>
        <p className={`mt-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
          {user?.email}
        </p>
      </div>

      {/* Section 1 — Profil */}
      <SectionCard icon={User} title={t('account.profile')} isDark={isDark}>
        <form onSubmit={handleSaveProfile} className="space-y-4">
          <div>
            <label htmlFor="account-name" className={labelCls}>{t('account.name')}</label>
            <input
              id="account-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className={inputCls}
              required
            />
          </div>
          <div>
            <label className={labelCls}>{t('account.email')}</label>
            <input
              type="email"
              value={user?.email || ''}
              readOnly
              className={`${inputCls} opacity-60 cursor-not-allowed`}
            />
            <p className={`text-xs mt-1.5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
              {t('account.emailReadonly')}
            </p>
          </div>
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={profileLoading}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors"
            >
              {profileLoading
                ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <Check className="w-4 h-4" />
              }
              {t('account.saveProfile')}
            </button>
          </div>
          <FeedbackMessage type={profileMsg?.type} message={profileMsg?.text} isDark={isDark} />
        </form>
      </SectionCard>

      {/* Section 2 — Sécurité */}
      <SectionCard icon={Lock} title={t('account.security')} isDark={isDark}>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <PasswordField
            id="current-password"
            label={t('account.currentPassword')}
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            isDark={isDark}
          />
          <PasswordField
            id="new-password"
            label={t('account.newPassword')}
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            isDark={isDark}
          />
          <PasswordField
            id="confirm-password"
            label={t('account.confirmPassword')}
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            isDark={isDark}
          />
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors"
            >
              {passwordLoading
                ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <Lock className="w-4 h-4" />
              }
              {t('account.changePassword')}
            </button>
          </div>
          <FeedbackMessage type={passwordMsg?.type} message={passwordMsg?.text} isDark={isDark} />
        </form>
      </SectionCard>

      {/* Section RGPD-2.1 — Mes données */}
      <SectionCard icon={Database} title={t('account.myData.title')} isDark={isDark}>
        {loadingDataSummary ? (
          <div className="flex items-center justify-center py-6">
            <span className="w-5 h-5 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
          </div>
        ) : !dataSummary ? (
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            {t('common.error')}
          </p>
        ) : (
          <div className="space-y-5">
            {/* Profil enrichi */}
            <div>
              <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                {t('account.myData.profile')}
              </h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <dt className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t('account.email')}</dt>
                <dd className={isDark ? 'text-slate-200' : 'text-gray-900'}>{dataSummary.profile.email}</dd>
                <dt className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t('account.name')}</dt>
                <dd className={isDark ? 'text-slate-200' : 'text-gray-900'}>{dataSummary.profile.name || '—'}</dd>
                <dt className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t('account.language')}</dt>
                <dd className={isDark ? 'text-slate-200' : 'text-gray-900'}>{dataSummary.profile.language === 'fr' ? 'Français' : 'Deutsch'}</dd>
                <dt className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t('account.myData.memberSince')}</dt>
                <dd className={isDark ? 'text-slate-200' : 'text-gray-900'}>{formatDate(dataSummary.profile.created_at)}</dd>
              </dl>
            </div>

            {/* Consentement */}
            <div className={`pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                {t('account.myData.consent')}
              </h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <dt className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t('account.myData.consentAcceptedOn')}</dt>
                <dd className={isDark ? 'text-slate-200' : 'text-gray-900'}>{formatDate(dataSummary.profile.consent_given_at)}</dd>
                <dt className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t('account.myData.consentVersions')}</dt>
                <dd className={isDark ? 'text-slate-200' : 'text-gray-900'}>
                  {dataSummary.profile.consent_versions
                    ? `CGU ${dataSummary.profile.consent_versions.terms || '?'} · Privacy ${dataSummary.profile.consent_versions.privacy || '?'}`
                    : '—'}
                </dd>
              </dl>
            </div>

            {/* Company */}
            {dataSummary.company && (
              <div className={`pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                <h3 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  <Building2 className="w-4 h-4" />
                  {t('account.myData.company')}
                </h3>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <dt className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t('account.name')}</dt>
                  <dd className={isDark ? 'text-slate-200' : 'text-gray-900'}>{dataSummary.company.name}</dd>
                  <dt className={isDark ? 'text-slate-400' : 'text-gray-500'}>{t('account.myData.yourRole')}</dt>
                  <dd className={isDark ? 'text-slate-200' : 'text-gray-900'}>{dataSummary.company.user_role_in_company || '—'}</dd>
                </dl>
              </div>
            )}

            {/* Statistics */}
            <div className={`pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                {t('account.myData.statistics')}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[
                  { icon: Calendar, label: t('account.myData.fiscalYears'), value: dataSummary.statistics.fiscal_years_count },
                  { icon: Package, label: t('account.myData.products'), value: dataSummary.statistics.products_count },
                  { icon: FileInput, label: t('account.myData.activities'), value: dataSummary.statistics.activities_count },
                  { icon: FlaskConical, label: t('account.myData.scenarios'), value: dataSummary.statistics.scenarios_count },
                  { icon: Target, label: t('account.myData.objectives'), value: dataSummary.statistics.carbon_objectives_count },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className={`p-3 rounded-xl text-center ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <Icon className={`w-5 h-5 mx-auto mb-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                    <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</p>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SectionCard>

      {/* Section RGPD-2.x — Confidentialité & RGPD */}
      <SectionCard icon={Shield} title={t('account.privacy.title')} isDark={isDark}>
        <p className={`text-sm mb-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
          {t('account.privacy.description')}
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            data-testid="rgpd-export-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('account.privacy.exporting')}
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                {t('account.privacy.export')}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            data-testid="rgpd-delete-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm bg-red-500 hover:bg-red-600 text-white transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            {t('account.privacy.delete')}
          </button>
        </div>
        {exportError && (
          <div
            className={`mt-3 flex items-start gap-2 p-3 rounded-xl text-sm ${
              isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-700'
            }`}
            data-testid="rgpd-export-error"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{exportError}</span>
          </div>
        )}
      </SectionCard>

      {/* RGPD-2.3 — Delete account modal */}
      <DeleteAccountModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDeleted={handleAccountDeleted}
        userEmail={user?.email}
        onSubmit={deleteAccount}
      />

      {/* Section 3 — Préférences */}
      <SectionCard icon={Settings2} title={t('account.preferences')} isDark={isDark}>
        <div className="space-y-4">
          {/* Langue */}
          <div className="flex items-center justify-between py-3 border-b border-inherit">
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('account.language')}</p>
              <p className={`text-sm mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                {language === 'fr' ? 'Français' : 'Deutsch'}
              </p>
            </div>
            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Globe className="w-4 h-4" />
              {language === 'fr' ? 'FR → DE' : 'DE → FR'}
            </button>
          </div>

          {/* Thème */}
          <div className="flex items-center justify-between py-3">
            <div>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('account.theme')}</p>
              <p className={`text-sm mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                {isDark
                  ? (language === 'fr' ? 'Mode sombre' : 'Dunkelmodus')
                  : (language === 'fr' ? 'Mode clair' : 'Hellmodus')
                }
              </p>
            </div>
            <button
              onClick={toggleTheme}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {isDark
                ? (language === 'fr' ? 'Passer en clair' : 'Zu Hell wechseln')
                : (language === 'fr' ? 'Passer en sombre' : 'Zu Dunkel wechseln')
              }
            </button>
          </div>
        </div>
      </SectionCard>
    </div>
  );
};

export default Account;
